// WHAT: Component for managing SwitchBot token and secret with secure storage
// WHY:  Provides interface for setting SwitchBot credentials for smart home integration
// DOMAIN: Settings → SwitchBot
// DEPENDENCIES: SwitchBot components and hooks
// RISKS:
//   - NOTE: Token and secret are displayed in plain text when editing - security risk
//   - NOTE: No validation for token/secret format - may allow invalid credentials

import { SecretSection, StatusMessage, TokenSection } from "./components";
import { useSwitchBotCredentials } from "./hooks";

/**
 * WHAT:
 *   Component that provides interface for managing SwitchBot token and secret
 *   with separate edit modes and secure storage.
 *
 * WHY:
 *   Enables users to configure SwitchBot credentials for smart home device
 *   integration with privacy protection (values hidden when not editing).
 *
 * BEHAVIOR:
 *   - Loads credentials on mount (values hidden)
 *   - Supports separate edit modes for token and secret
 *   - Saves credentials to secure storage
 *   - Allows deleting credentials
 *   - Shows configuration status
 *
 * RISKS:
 *   - NOTE: Token and secret are displayed in plain text when editing - security risk
 *   - NOTE: No validation for token/secret format - may allow invalid credentials
 *
 * @returns {JSX.Element} SwitchBot token settings component
 *
 */
// Reason: Component is used via routing in src/routes/index.tsx, not directly imported

export function SwitchBotTokenSettings() {
  const {
    editingSecret,
    editingToken,
    handleRemoveSecret,
    handleRemoveToken,
    handleSaveSecret,
    handleSaveToken,
    loading,
    message,
    savedSecret,
    savedToken,
    secret,
    setEditingSecret,
    setEditingToken,
    setSecret,
    setToken,
    token,
  } = useSwitchBotCredentials();

  return (
    <div className="space-y-6">
      <TokenSection
        editingToken={editingToken}
        loading={loading}
        onCancelEdit={() => {
          setEditingToken(false);
          setToken("");
        }}
        onEdit={() => {
          setEditingToken(true);
        }}
        onRemove={handleRemoveToken}
        onSave={handleSaveToken}
        onTokenChange={setToken}
        {...(savedToken === undefined ? {} : { savedToken: savedToken ?? null })}
        token={token}
      />

      <SecretSection
        editingSecret={editingSecret}
        loading={loading}
        onCancelEdit={() => {
          setEditingSecret(false);
          setSecret("");
        }}
        onEdit={() => {
          setEditingSecret(true);
        }}
        onRemove={handleRemoveSecret}
        onSave={handleSaveSecret}
        onSecretChange={setSecret}
        {...(savedSecret === undefined ? {} : { savedSecret: savedSecret ?? null })}
        secret={secret}
      />

      {message !== undefined && <StatusMessage message={message} />}
    </div>
  );
}
